// ABOUTME: Modal component for creating OOH advertising campaigns.
// ABOUTME: Handles date range selection, POI assignment, and campaign submission via CampaignCreationService.
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { toDate, addDays, addWeeks, formatDate, toYMD } from 'app/utils/date'

import { Delivery } from '../models/Vertical'
import { ContextService } from '../services'
import { injectCreateCampaignMutation } from '../mobility/campaigns/models/campaign.model'

type CampaignType = 'deliveries' | 'days'

@Component({
  standalone: false,
  selector: 'app-campaign-creation-modal',
  templateUrl: './campaign-creation-modal.component.html',
  styleUrls: ['./campaign-creation-modal.component.scss'],
})
export class CampaignCreationModalComponent implements OnInit {
  @Input() pois = []
  @Output() onCancel = new EventEmitter()
  @Output() onConfirm = new EventEmitter()

  campaignName = ''
  campaignType: CampaignType = 'deliveries'
  sizeOptions = [1, 2, 3, 4, 5, 6].map((n) => ({ label: `${n}x`, value: n }))
  selectedSize = this.sizeOptions[0]
  loading = false

  startDate = new Date(this.minDeliveryDate)
  endDate = new Date(this.maxDeliveryDate)

  startDelivery = null
  showConfirmation = false
  campaign = null

  get disabled() {
    return !this.campaignName
  }

  get maxDeliveryDate() {
    const times = this.verticalDeliveries.map((d) =>
      new Date(d.finish.split('T')[0] + 'T12:00:00').getTime(),
    )
    const minTime = Math.max(...times)
    return new Date(minTime)
  }

  get minDeliveryDate() {
    const times = this.verticalDeliveries.map((d) =>
      new Date(d.start.split('T')[0] + 'T12:00:00').getTime(),
    )
    const maxTime = Math.min(...times)
    return new Date(maxTime)
  }

  get verticalDeliveries() {
    const deliveries =
      this.context.vertical &&
      this.context.vertical.deliveries[this.context.city.name]
    return deliveries || []
  }

  get deliveries() {
    return this.verticalDeliveries.map((delivery: Delivery) => {
      if (delivery.granularity === 'week') {
        return {
          label: this.createWeeklyDeliveryLabel(delivery),
          value: delivery.id,
        }
      } else {
        const d = toDate(delivery.start)
        const month = formatDate(d, { month: 'long' })
        const year = d.getFullYear()
        return {
          label: delivery.name || `${month} ${year}`,
          value: delivery.id,
        }
      }
    })
  }

  get dateRangeFromDelivery() {
    const startDelivery = this.startDelivery || this.deliveries[0]
    const delivery = this.verticalDeliveries.find(
      (d) => d.id === startDelivery.value,
    )
    const startDate = toYMD(toDate(delivery.start))
    const endDate = toYMD(
      addDays(
        addWeeks(toDate(delivery.start), this.selectedSize.value * 2),
        -1,
      ),
    )
    return { startDate, endDate }
  }

  get dateRangeFromDatepicker() {
    return {
      startDate: toYMD(this.startDate),
      endDate: toYMD(this.endDate),
    }
  }

  get campaignModel() {
    const dates =
      this.campaignType === 'deliveries'
        ? this.dateRangeFromDelivery
        : this.dateRangeFromDatepicker
    return {
      start_date: dates.startDate,
      end_date: dates.endDate,
      poi_ids: this.pois,
      org_id: this.context.organization.id,
      delivery_name: this.campaignName,
      delivery_id: this.context.vertical.delivery.id,
    }
  }

  private createCampaignMutation = injectCreateCampaignMutation()

  constructor(
    private context: ContextService,
    private translateService: TranslateService,
    private changes: ChangeDetectorRef,
  ) {}

  ngOnInit() {
    this.showConfirmation = false
    this.campaign = null
  }

  createWeeklyDeliveryLabel(delivery) {
    const s = toDate(delivery.start)
    const f = toDate(delivery.finish)
    const year = s.getFullYear()
    const from = formatDate(s, { day: 'numeric', month: 'short' })
    const to = formatDate(f, { day: 'numeric', month: 'short' })
    return `${year} / ${from} - ${to}`
  }

  confirm() {
    this.onConfirm.emit(this.campaign)
  }

  onChangeSize(size) {
    this.selectedSize = size
    console.log(size)
  }

  onChangeCampaignType($event, type) {
    if ($event.checked) {
      this.campaignType = type
    } else {
      this.campaignType = type === 'deliveries' ? 'days' : 'deliveries'
    }
  }

  save() {
    this.loading = true
    this.createCampaignMutation.mutate(this.campaignModel, {
      onSuccess: (result) => {
        this.campaign = result
        this.showConfirmation = true
        this.loading = false
        this.changes.detectChanges()
      },
      onError: (err) => {
        this.campaign = null
        console.error(
          '[campaign-creation-modal.component.ts] Error creating campaign',
          err,
        )
        this.loading = false
        this.changes.detectChanges()
      },
    })
  }

  cancel() {
    this.onCancel.emit()
  }

  onSelectTarget(size) {
    this.selectedSize = size
  }

  onSelectStartDelivery(delivery) {
    this.startDelivery = delivery
  }
}
